/**
 * Route Utilities
 * Validates routes and checks user access before navigation
 */

/**
 * List of valid routes that exist in the app (from App.jsx)
 * These routes are valid even if not in nav items
 */
const VALID_APP_ROUTES = [
  '/dashboard',
  '/appointments',
  '/physicians',
  '/messages',
  '/video-call',
  '/prescriptions',
  '/profile',
  '/settings',
  '/documentation',
  '/contact',
  '/notes',
  '/admin',
  '/admin/security',
  '/admin/admins',
  '/clinic-dashboard',
  '/clinic/patients',
  '/clinic-staff-dashboard',
  '/physician/patients',
  '/physician/records',
  '/physician/profile',
  '/physician/settings',
  '/sponsor-dashboard',
  '/sponsor/family',
  '/sponsor/payments',
  '/sponsor/profile',
  '/sponsor/settings',
  '/sponsor/contact',
  '/admin/physicians',
  '/admin/patients',
  '/admin/sponsors',
  '/admin/settings',
];

/**
 * Check if a route exists in navigation items or is a valid app route
 * @param {string} route - Route path to check
 * @param {Array} navItems - Navigation items array
 * @returns {boolean} - Whether route exists
 */
export const routeExists = (route, navItems) => {
  if (!route) {
    return false;
  }

  // Normalize route (remove query params and hash)
  const normalizedRoute = route.split('?')[0].split('#')[0];

  // First check if it's a valid app route (more lenient)
  if (VALID_APP_ROUTES.includes(normalizedRoute)) {
    return true;
  }

  // Then check navigation items
  if (navItems && navItems.length > 0) {
    for (const item of navItems) {
      // Check main item path
      if (item.path === normalizedRoute || item.path === route) {
        return true;
      }

      // Check children paths
      if (item.children && Array.isArray(item.children)) {
        for (const child of item.children) {
          if (child.path === normalizedRoute || child.path === route) {
            return true;
          }
        }
      }
    }
  }

  return false;
};

/**
 * Check if user has access to a route based on role requirements
 * @param {string} route - Route path
 * @param {Array} navItems - Navigation items array
 * @param {object} userProfile - User profile with role information
 * @param {object} authHelpers - Auth helper functions (isAdmin, isProvider, etc.)
 * @returns {boolean} - Whether user has access
 */
export const hasRouteAccess = (route, navItems, userProfile, authHelpers = {}) => {
  if (!route || !navItems || navItems.length === 0) {
    return false;
  }

  // Normalize route
  const normalizedRoute = route.split('?')[0].split('#')[0];

  // Find the navigation item for this route
  const findNavItem = (items, targetRoute) => {
    for (const item of items) {
      if (item.path === targetRoute || item.path === normalizedRoute) {
        return item;
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.path === targetRoute || child.path === normalizedRoute) {
            return child;
          }
        }
      }
    }
    return null;
  };

  const navItem = findNavItem(navItems, normalizedRoute);

  // If route not found in nav items, assume it's accessible (might be a direct route)
  if (!navItem) {
    return true; // Let the route handle its own access control
  }

  // Check if item requires specific role
  if (navItem.requiresRole) {
    const { isAdmin, isProvider, isSponsor } = authHelpers;
    const userRole = userProfile?.tenant_role || userProfile?.platform_role;

    switch (navItem.requiresRole) {
      case 'admin':
      case 'super_admin':
        return isAdmin || false;
      case 'physician':
      case 'provider':
        return isProvider || false;
      case 'sponsor':
        return isSponsor || false;
      default:
        return userRole === navItem.requiresRole;
    }
  }

  // No role requirement, accessible
  return true;
};

/**
 * Validate route before navigation
 * Checks both existence and access
 * @param {string} route - Route path to validate
 * @param {Array} navItems - Navigation items array
 * @param {object} userProfile - User profile
 * @param {object} authHelpers - Auth helper functions
 * @returns {object} - Validation result
 */
export const validateRoute = (route, navItems, userProfile, authHelpers = {}) => {
  const exists = routeExists(route, navItems);
  const hasAccess = hasRouteAccess(route, navItems, userProfile, authHelpers);

  return {
    valid: exists && hasAccess,
    exists,
    hasAccess,
    route
  };
};

/**
 * Get default route for user based on role
 * @param {object} userProfile - User profile
 * @param {object} authHelpers - Auth helper functions
 * @returns {string} - Default route path
 */
export const getDefaultRouteForUser = (userProfile, authHelpers = {}) => {
  if (!userProfile) {
    return '/dashboard';
  }

  const { isAdmin, isProvider } = authHelpers;

  // Admins go to admin dashboard
  if (isAdmin) {
    return '/admin';
  }

  // Physicians go to clinic dashboard
  if (isProvider) {
    return '/clinic-dashboard';
  }

  // Sponsors go to sponsor dashboard
  if (userProfile.tenant_role === 'sponsor') {
    return '/sponsor-dashboard';
  }

  // Default to patient dashboard
  return '/dashboard';
};

/**
 * Get safe redirect route (validates before returning)
 * @param {string} preferredRoute - Preferred route
 * @param {Array} navItems - Navigation items
 * @param {object} userProfile - User profile
 * @param {object} authHelpers - Auth helper functions
 * @returns {string} - Safe route to redirect to
 */
export const getSafeRedirectRoute = (
  preferredRoute,
  navItems,
  userProfile,
  authHelpers = {}
) => {
  // If no preferred route, use default
  if (!preferredRoute || preferredRoute === '#') {
    return getDefaultRouteForUser(userProfile, authHelpers);
  }

  // Validate preferred route
  const validation = validateRoute(preferredRoute, navItems, userProfile, authHelpers);

  if (validation.valid) {
    return preferredRoute;
  }

  // If invalid, fall back to default
  if (import.meta.env.DEV) {
    console.warn(`Invalid route "${preferredRoute}":`, validation);
  }

  return getDefaultRouteForUser(userProfile, authHelpers);
};

